import { useSearchParams } from 'next/navigation';
import { isValid, parse } from 'date-fns';
import { SearchModes } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { buildAccommodationInterval } from '@/app/components/hotel-detail/common/common.helper';
import { getNights, URL_DATE_FORMAT } from '@/app/components/hotel-detail/common/dates';

// buildAccommodationInterval only validates the shape, not the calendar ("2026-02-30" passes).
// The request paths degrade gracefully on such a value, but here it would reach intlFormat as an
// Invalid Date and throw, so reject it like the old provider dates were isValid-checked.
function isRealDate(yyyymmdd: string) {
  return isValid(parse(yyyymmdd, URL_DATE_FORMAT, new Date()));
}

/**
 * The hotel stay to display (yyyyMMdd), which on a DP partial stay is shorter than the
 * trip: the URL checkin/checkout are the flight dates. Sources, in order — each taken as a
 * whole (dates + nights together) so a row can never mix the dates of one source with the
 * nights of another:
 * 1. the offers (`searchSummary`) — the definitive stay once the rooms response arrives,
 *    trusted only when it carried dates: on the empty-dates branch selectOffersFromRoomsData
 *    falls back to the trip nights, which must not be paired with prefilter stay dates;
 * 2. the `prefilter_checkin_checkout` URL param — the same dates the pricing/rooms
 *    requests send as `accommodation`, so the first paint already shows the stay
 *    instead of flashing the flight dates while rooms load;
 * 3. the URL trip dates — no partial stay (or a malformed prefilter param).
 */
export function useHotelStayDates() {
  const { searchInfoData } = usePageWrapperData();
  const { checkin: urlCheckin, checkout: urlCheckout, nights: urlNights } = useUrlSearchParams();
  const { offersData } = useOffersContext();
  const nextSearchParams = useSearchParams();

  if (offersData?.checkin && offersData?.checkout) {
    return {
      checkin: offersData.checkin,
      checkout: offersData.checkout,
      nights: offersData.nights || getNights(offersData.checkin, offersData.checkout),
    };
  }

  // updateUrl() rewrites the URL with window.history.replaceState and intentionally does not
  // sync the Next.js router (SLIDERS-2188), so after an in-page date change useSearchParams()
  // keeps returning the removed param: read the live window.location on the client instead
  // (useSearchParams covers SSR; both see the same URL at hydration).
  const customCheckinCheckout = canUseDOM
    ? getSearchParams()[URLParameters.CUSTOM_CHECKIN_CHECKOUT]
    : nextSearchParams.get(URLParameters.CUSTOM_CHECKIN_CHECKOUT);
  const accommodation = buildAccommodationInterval(customCheckinCheckout, searchInfoData.searchMode === SearchModes.DP);
  // yyyy-MM-dd -> yyyyMMdd, the format of the URL dates and of offersData.checkin/checkout.
  const prefilterCheckin = accommodation?.start.date.replaceAll('-', '');
  const prefilterCheckout = accommodation?.end.date.replaceAll('-', '');

  if (prefilterCheckin && prefilterCheckout && isRealDate(prefilterCheckin) && isRealDate(prefilterCheckout)) {
    return {
      checkin: prefilterCheckin,
      checkout: prefilterCheckout,
      nights: getNights(prefilterCheckin, prefilterCheckout),
    };
  }

  return { checkin: urlCheckin, checkout: urlCheckout, nights: urlNights };
}
